import React, { useState, useEffect, useCallback } from 'react'
import {
  Stack,
  Button,
  SimpleModal,
  FormControl,
  Text,
  DateChooser,
  Checkbox
} from '@/components/lib'
import api from '@/api'
import utils from '@/utils/utils'
import StageScheduleResetPopover from './StageScheduleResetPopover'

export default function StageScheduleOverrideModal ({
  isOpen,
  onClose,
  trialId,
  participantId,
  /** Stage row from `listStages` (non–ad-hoc timepoint). */
  stage,
  onSaved
}) {
  const [enableAt, setEnableAt] = useState(null)
  const [saving, setSaving] = useState(false)
  const [resetOpen, setResetOpen] = useState(false)
  const [applyToAllRecurrences, setApplyToAllRecurrences] = useState(false)

  const isMultiRecurrenceStage =
    !!stage?.isRecurring && (stage?.recurringDuration ?? 0) > 1

  useEffect(() => {
    if (!isOpen || !stage) return
    const t = stage.enableAt ? new Date(stage.enableAt) : new Date()
    setEnableAt(Number.isFinite(t.getTime()) ? t : new Date())
    setApplyToAllRecurrences(false)
  }, [isOpen, stage])

  const handleClose = useCallback(() => {
    if (!saving) onClose?.()
  }, [saving, onClose])

  const handleSave = useCallback(() => {
    if (!trialId || !participantId || !stage) return
    if (!enableAt) {
      utils.toast('error', 'Please choose a date and time')
      return
    }
    setSaving(true)
    api.participants.upsertStageOverride(
      trialId,
      participantId,
      {
        stage: utils.timelineStageMongoId(stage),
        stageRecurrence: stage.recurrence != null ? stage.recurrence : 0,
        enableAt: enableAt instanceof Date ? enableAt.toISOString() : enableAt,
        ...(isMultiRecurrenceStage && applyToAllRecurrences
          ? { applyToAllRecurrences: true }
          : {})
      },
      () => {
        setSaving(false)
        utils.toast('success', 'Schedule updated')
        onSaved?.()
        onClose?.()
      },
      (err) => {
        setSaving(false)
        utils.toast('error', err.message || 'Could not update schedule')
      }
    )
  }, [
    trialId,
    participantId,
    stage,
    enableAt,
    applyToAllRecurrences,
    isMultiRecurrenceStage,
    onSaved,
    onClose
  ])

  if (!stage) return null

  const recurrenceLabel =
    stage.recurrence == null
      ? 'Not recurring'
      : `Recurrence ${stage.recurrence + 1}`

  return (
    <>
      <SimpleModal
        size='sm'
        isOpen={isOpen}
        onClose={handleClose}
        header='Reschedule stage for this participant'
        body={
          <Stack gap={4}>
            <Stack gap={1}>
              <Text fontWeight='semibold'>{stage.name}</Text>
              <Text fontSize='sm' color='fg.subtle'>{recurrenceLabel}</Text>
            </Stack>
            <FormControl label='Make available from' required>
              <Stack direction='row' gap={2} alignItems='center'>
                <DateChooser
                  withTime
                  value={enableAt}
                  onChange={(d) => setEnableAt(d)}
                  placeholder='Select date and time'
                />
                <Text fontSize='sm' color='fg.subtle'>{Intl.DateTimeFormat().resolvedOptions().timeZone}</Text>
              </Stack>
            </FormControl>
            {isMultiRecurrenceStage && (
              <Checkbox
                checked={applyToAllRecurrences}
                onChange={(e) => setApplyToAllRecurrences(!!e.checked)}
              >
                Also shift other recurrences of this stage
              </Checkbox>
            )}
            {stage.hasScheduleOverride && (
              <Button
                variant='outline'
                alignSelf='flex-start'
                onClick={() => setResetOpen(true)}
                disabled={saving}
              >
                Reset schedule
              </Button>
            )}
          </Stack>
      }
        footer={
          <Stack direction='row' gap={2} justifyContent='flex-end'>
            <Button variant='ghost' onClick={handleClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              colorPalette='primary'
              onClick={handleSave}
              loading={saving}
            >
              Save
            </Button>
          </Stack>
      }
      />
      <StageScheduleResetPopover
        isOpen={resetOpen}
        onClose={() => setResetOpen(false)}
        trialId={trialId}
        participantId={participantId}
        stage={stage}
        onReset={() => { onSaved?.(); onClose?.() }}
      />
    </>
  )
}
